import { useState, useCallback, useMemo } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, Check, Search, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { jsonToPlainText } from 'json-to-plain-text'

interface JsonViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    content: string | null
}

function HighlightedText({ text, search }: { text: string; search: string }) {
    if (!search) return <>{text}</>

    const parts: Array<{ text: string; match: boolean }> = []
    const lower = text.toLowerCase()
    const searchLower = search.toLowerCase()
    let lastIndex = 0

    let idx = lower.indexOf(searchLower)
    while (idx !== -1) {
        if (idx > lastIndex) {
            parts.push({ text: text.slice(lastIndex, idx), match: false })
        }
        parts.push({ text: text.slice(idx, idx + search.length), match: true })
        lastIndex = idx + search.length
        idx = lower.indexOf(searchLower, lastIndex)
    }
    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex), match: false })
    }

    return (
        <>
            {parts.map((part, i) =>
                part.match ? (
                    <mark key={i} className="bg-yellow-300 dark:bg-yellow-600 text-foreground rounded-sm px-0.5">
                        {part.text}
                    </mark>
                ) : (
                    <span key={i}>{part.text}</span>
                )
            )}
        </>
    )
}

/**
 * Parse once, and say whether it worked.
 *
 * Both views used to swallow the parse failure and silently render the raw string. That is what
 * made a truncated payload so hard to read: the JSON view showed a blob ending mid-word and the
 * text view showed the identical blob, so the screen looked like a rendering or scrolling fault
 * when the data itself was cut off. `ok: false` is the honest answer and the dialog says so.
 */
function parseOrRaw(jsonStr: string): { ok: boolean; value: unknown; raw: string } {
    try {
        return { ok: true, value: JSON.parse(jsonStr), raw: jsonStr }
    } catch {
        return { ok: false, value: null, raw: jsonStr }
    }
}

function HighlightedJson({ content: jsonStr, search }: { content: string; search: string }) {
    const formatted = useMemo(() => {
        const p = parseOrRaw(jsonStr)
        return p.ok ? JSON.stringify(p.value, null, 2) : p.raw
    }, [jsonStr])

    if (!search) {
        return <pre className="text-xs font-mono whitespace-pre-wrap break-words">{formatted}</pre>
    }

    const lines = formatted.split('\n')
    return (
        <pre className="text-xs font-mono whitespace-pre-wrap break-words">
            {lines.map((line, i) => (
                <span key={i}>
                    {line.toLowerCase().includes(search.toLowerCase()) ? (
                        <HighlightedText text={line} search={search} />
                    ) : (
                        line
                    )}
                    {i < lines.length - 1 ? '\n' : ''}
                </span>
            ))}
        </pre>
    )
}

function HighlightedPlainText({ content: jsonStr, search }: { content: string; search: string }) {
    const plainText = useMemo(() => {
        const p = parseOrRaw(jsonStr)
        // Nothing to lay out as text when it will not parse — showing the raw string here is what
        // made "the text view doesn't work" the reported symptom of a truncated payload.
        return p.ok ? jsonToPlainText(p.value) : p.raw
    }, [jsonStr])

    if (!search) {
        return <pre className="text-xs font-mono whitespace-pre-wrap break-words">{plainText}</pre>
    }

    const lines = plainText.split('\n')
    return (
        <pre className="text-xs font-mono whitespace-pre-wrap break-words">
            {lines.map((line, i) => (
                <span key={i}>
                    {line.toLowerCase().includes(search.toLowerCase()) ? (
                        <HighlightedText text={line} search={search} />
                    ) : (
                        line
                    )}
                    {i < lines.length - 1 ? '\n' : ''}
                </span>
            ))}
        </pre>
    )
}

export function JsonViewDialog({ open, onOpenChange, title, content }: JsonViewDialogProps) {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'json' | 'text'>('json')
    const [copied, setCopied] = useState(false)
    const [search, setSearch] = useState('')

    const matchCount = useMemo(() => {
        if (!search || !content) return 0
        const searchLower = search.toLowerCase()
        let text: string
        try {
            text = viewMode === 'json'
                ? JSON.stringify(JSON.parse(content), null, 2)
                : jsonToPlainText(JSON.parse(content))
        } catch {
            text = content
        }
        const lower = text.toLowerCase()
        let count = 0
        let idx = lower.indexOf(searchLower)
        while (idx !== -1) {
            count++
            idx = lower.indexOf(searchLower, idx + searchLower.length)
        }
        return count
    }, [search, content, viewMode])

    const handleCopy = useCallback(() => {
        if (!content) return
        let textToCopy: string
        try {
            if (viewMode === 'json') {
                textToCopy = JSON.stringify(JSON.parse(content), null, 2)
            } else {
                textToCopy = jsonToPlainText(JSON.parse(content))
            }
        } catch {
            textToCopy = content
        }
        navigator.clipboard.writeText(textToCopy)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [content, viewMode])

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) setSearch('')
        onOpenChange(open)
    }, [onOpenChange])

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{t('objects.versions.viewJsonData')}</DialogDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 border rounded-md p-1">
                                <Button
                                    variant={viewMode === 'json' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="h-7 px-3 text-xs"
                                    onClick={() => setViewMode('json')}
                                >
                                    JSON
                                </Button>
                                <Button
                                    variant={viewMode === 'text' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="h-7 px-3 text-xs"
                                    onClick={() => setViewMode('text')}
                                >
                                    {t('objects.versions.text')}
                                </Button>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2"
                                onClick={handleCopy}
                            >
                                {copied ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogHeader>
                {/* Search field */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-8 text-sm"
                    />
                    {search && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            {matchCount} {matchCount === 1 ? 'match' : 'matches'}
                        </span>
                    )}
                </div>
                {content && !parseOrRaw(content).ok && (
                    <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        <span>
                            {t('objects.versions.invalidJson',
                                'This record is not valid JSON — it looks truncated. What is stored is shown below in full; there is nothing further to scroll to.')}
                        </span>
                    </div>
                )}
                {/* min-h-0 is what makes this scroll. A flex child's default min-height is its
                    content, so without it the box grows to fit and the DIALOG clips instead —
                    no scrollbar anywhere, and the content simply stops. */}
                <div className="flex-1 min-h-0 overflow-auto bg-muted rounded-lg p-4">
                    {!content ? (
                        <span className="text-muted-foreground">-</span>
                    ) : viewMode === 'json' ? (
                        <HighlightedJson content={content} search={search} />
                    ) : (
                        <HighlightedPlainText content={content} search={search} />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
